import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/services/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/AuthShell";
import { ModeToggle, PhoneField } from "@/components/PhoneField";
import { TwoFactorSetup } from "@/components/TwoFactorSetup";
import { twofaService } from "@/services/twofaService";
import { useLanguage } from "@/context/LanguageContext";

const homeFor = (role) => {
  if (role === "admin") return "/admin";
  if (role === "supervisor" || role === "manager" || role === "agent") return "/manager";
  return "/terminal";
};


export default function LoginPage() {
  const { t } = useLanguage();
  const { login, completeLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState("");
  const [setupToken, setSetupToken] = useState("");
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [mode, setMode] = useState("email");
  const [dial, setDial] = useState("+55");
  const [phone, setPhone] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await login(mode === "phone" ? { phone_dial: dial, phone } : { email }, password);
      if (res.requires_2fa) {
        setPending(res.pending_token);
      } else if (res.requires_2fa_setup) {
        setSetupToken(res.setup_token);
      } else {
        navigate(homeFor(res.user.role), { replace: true });
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await twofaService.verify(pending, code);
      completeLogin(u);
      navigate(homeFor(u.role), { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (setupToken) {
    return (
      <AuthShell overline={t("auth.setupOverline")} title={t("auth.setupTitle")}
                 subtitle={t("auth.setupSubtitle")}>
        <TwoFactorSetup
          setupToken={setupToken}
          onDone={async () => {
            const res = await login(mode === "phone" ? { phone_dial: dial, phone } : { email }, password);
            if (res.requires_2fa) { setSetupToken(""); setPending(res.pending_token); }
          }}
        />
      </AuthShell>
    );
  }

  if (pending) {
    return (
      <AuthShell overline={t("auth.twofaOverline")} title={t("auth.twofaTitle")}
                 subtitle={t("auth.twofaSubtitle")}>
        <form onSubmit={verify} className="space-y-6" data-testid="twofa-login-form">
          <Input
            autoFocus
            required
            data-testid="twofa-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().trim())}
            placeholder={useRecovery ? t("auth.recoveryCode") : "000000"}
            maxLength={useRecovery ? 24 : 6}
            className="h-14 rounded-md border-[#22252F] bg-[#0F1116] text-center font-mono text-xl tracking-[0.4em] text-white"
          />
          {error ? (
            <p data-testid="twofa-error" className="rounded-md border border-[#EF4444]/40 bg-[#EF4444]/10 px-4 py-3 text-sm text-[#FCA5A5]">
              {error}
            </p>
          ) : null}
          <Button type="submit" disabled={busy} data-testid="twofa-submit-button"
                  className="h-12 w-full rounded-md bg-[#2563EB] text-sm font-semibold uppercase tracking-[0.15em] hover:bg-[#1D4ED8]">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.verify")}
          </Button>
          <button type="button" data-testid="use-recovery-code"
                  onClick={() => { setUseRecovery(!useRecovery); setCode(""); setError(""); }}
                  className="text-sm text-[#94A3B8] underline transition-colors hover:text-white">
            {useRecovery ? t("auth.useAuthenticator") : t("auth.useRecovery")}
          </button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      subtitle={t("auth.loginSubtitle")}
      overline={t("auth.overline")}
      title={t("auth.loginTitle")}
    >
      <form onSubmit={submit} className="space-y-6" data-testid="login-form">
        <ModeToggle mode={mode} onChange={(m) => { setMode(m); setError(""); }} testIdPrefix="login" />

        {mode === "phone" ? (
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">{t("auth.phone")}</Label>
            <PhoneField required dial={dial} phone={phone} onDial={setDial} onPhone={setPhone} testIdPrefix="login-phone" />
          </div>
        ) : (
        <div className="space-y-2">
          <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
            {t("auth.email")}
          </Label>
          <Input
            id="email"
            type="email"
            required
            autoComplete="email"
            data-testid="login-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="nome@dominio.com"
            className="h-12 rounded-md border-[#22252F] bg-[#0F1116] text-white placeholder:text-[#4B5563] focus-visible:ring-[#2563EB]"
          />
        </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
            {t("auth.password")}
          </Label>
          <Input
            id="password"
            type="password"
            required
            autoComplete="current-password"
            data-testid="login-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="h-12 rounded-md border-[#22252F] bg-[#0F1116] text-white placeholder:text-[#4B5563] focus-visible:ring-[#2563EB]"
          />
        </div>

        {error ? (
          <p
            data-testid="login-error"
            className="rounded-md border border-[#EF4444]/40 bg-[#EF4444]/10 px-4 py-3 text-sm text-[#FCA5A5]"
          >
            {error}
          </p>
        ) : null}

        <Button
          type="submit"
          disabled={busy}
          data-testid="login-submit-button"
          className="group h-12 w-full rounded-md bg-[#2563EB] text-sm font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:bg-[#1D4ED8]"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.signIn")}
          {!busy && <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />}
        </Button>

        <p className="text-sm text-[#94A3B8]">
          <Link
            to="/register"
            data-testid="go-to-register-link"
            className="font-semibold text-white underline decoration-[#2563EB] decoration-2 underline-offset-4 transition-colors hover:text-[#60A5FA]"
          >
            {t("auth.createAccount")}
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
